// In-workspace HTTP client for the server running inside WebContainer.
// Requests are sent from INSIDE the container (localhost, no CORS) via
// `containerRequest`; a direct browser fetch is the fallback.
import React, { useEffect, useRef, useState } from 'react';
import { Send, Loader2, XCircle, Copy, Code2, Sparkles, Plus, Trash2, Check } from 'lucide-react';
import MonacoCodeEditor from './MonacoCodeEditor';

export interface ApiRequestShape {
  id: string;
  name: string;
  method: string;
  url: string;
  headers?: { [key: string]: string };
  params?: { [key: string]: string };
  pathVars?: { [key: string]: string };
  body?: string;
  description?: string;
  lastResponse?: any;
  lastError?: string | null;
}

interface ApiTesterProps {
  request: ApiRequestShape;
  baseUrl?: string | null;
  /** Origin (e.g. http://localhost:3001) of the live server; the displayed URL
   * auto-syncs its host/port to this so it always matches the running server. */
  liveOrigin?: string | null;
  containerRequest?: (args: {
    method: string;
    path: string;
    headers: { [k: string]: string };
    body?: string;
  }) => Promise<{ status: number; statusText: string; headers: any; data: any; time: number; size: number }>;
  /** Persist edits (method/url/headers/params/body) back to the saved request. */
  onChange?: (patch: Partial<ApiRequestShape>) => void;
  onResponse?: (response: any) => void;
}

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

const methodColor = (m: string) =>
  m === 'GET' ? 'api-method--get'
    : m === 'POST' ? 'api-method--post'
      : m === 'PUT' ? 'api-method--put'
        : m === 'PATCH' ? 'api-method--patch'
          : m === 'DELETE' ? 'api-method--delete'
            : '';

const inputCls =
  'px-2 py-1.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded outline-none text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/40';

const ResponsePanel: React.FC<{ response: any }> = ({ response }) => {
  const [resTab, setResTab] = useState<'body' | 'headers'>('body');
  const [viewMode, setViewMode] = useState<'json' | 'preview' | 'raw'>('json');
  const [resCopied, setResCopied] = useState(false);

  const statusColor = (s?: number) =>
    !s ? 'text-gray-500' : s < 300 ? 'text-emerald-500' : s < 400 ? 'text-amber-500' : 'text-rose-500';

  const statusBg = (s?: number) =>
    !s ? 'bg-gray-500/10' : s < 300 ? 'bg-emerald-500/10' : s < 400 ? 'bg-amber-500/10' : 'bg-rose-500/10';

  const bodyText = typeof response.data === 'string' ? response.data : JSON.stringify(response.data, null, 2);

  const copyBody = () => {
    navigator.clipboard.writeText(bodyText);
    setResCopied(true);
    setTimeout(() => setResCopied(false), 1500);
  };

  const responseHeaders = response.headers || {};

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
      {/* Status bar */}
      <div className="flex items-center gap-3 px-3 py-2 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40">
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-bold ${statusColor(response.status)} ${statusBg(response.status)}`}>
          {response.status} {response.statusText}
        </span>
        <span className="text-[11px] text-gray-400 font-mono">{response.time} ms</span>
        <span className="text-[11px] text-gray-400 font-mono">{response.size} B</span>
      </div>

      {/* Tabs: Body | Headers */}
      <div className="flex items-center border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-0.5 px-2">
          {(['body', 'headers'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setResTab(t)}
              className={`px-3 py-1.5 text-xs font-medium capitalize border-b-2 -mb-px transition-colors ${resTab === t ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'}`}
            >
              {t}
              {t === 'headers' && Object.keys(responseHeaders).length > 0 && (
                <span className="ml-1 text-[10px] text-gray-400">({Object.keys(responseHeaders).length})</span>
              )}
            </button>
          ))}
        </div>

        {/* View mode toggles + copy (only for body) */}
        {resTab === 'body' && (
          <div className="ml-auto flex items-center gap-1 px-2">
            {(['json', 'preview', 'raw'] as const).map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-2 py-1 text-[11px] font-medium rounded transition-colors ${viewMode === mode
                  ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white'
                  : 'text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
                  }`}
              >
                {mode === 'json' ? 'JSON' : mode === 'preview' ? 'Preview' : 'Raw'}
              </button>
            ))}
            <div className="w-px h-4 bg-gray-200 dark:bg-gray-700 mx-1" />
            <button
              onClick={copyBody}
              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              title="Copy response body"
            >
              {resCopied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
              {resCopied ? 'Copied' : 'Copy'}
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="max-h-72 overflow-auto">
        {resTab === 'body' && (
          <>
            {viewMode === 'json' && (
              <pre className="p-3 text-xs font-mono text-emerald-600 dark:text-emerald-400 whitespace-pre-wrap leading-relaxed">
                {bodyText}
              </pre>
            )}
            {viewMode === 'preview' && (
              <div className="p-3">
                {typeof response.data === 'string' && response.data.trim().startsWith('<') ? (
                  <iframe
                    srcDoc={response.data}
                    className="w-full h-60 border border-gray-200 dark:border-gray-700 rounded bg-white"
                    sandbox=""
                    title="Response preview"
                  />
                ) : (
                  <pre className="text-xs font-mono text-gray-600 dark:text-gray-300 whitespace-pre-wrap">{bodyText}</pre>
                )}
              </div>
            )}
            {viewMode === 'raw' && (
              <pre className="p-3 text-xs font-mono text-gray-600 dark:text-gray-300 whitespace-pre-wrap break-all">
                {typeof response.data === 'string' ? response.data : JSON.stringify(response.data)}
              </pre>
            )}
          </>
        )}

        {resTab === 'headers' && (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {Object.entries(responseHeaders).length === 0 ? (
              <p className="p-3 text-xs text-gray-400 italic">No headers received.</p>
            ) : (
              Object.entries(responseHeaders).map(([key, value]) => (
                <div key={key} className="flex items-baseline gap-3 px-3 py-1.5">
                  <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-400 min-w-[140px]">{key}</span>
                  <span className="text-[11px] font-mono text-gray-800 dark:text-gray-200 break-all">{String(value)}</span>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ApiTester: React.FC<ApiTesterProps> = ({ request, baseUrl, liveOrigin, containerRequest, onChange, onResponse }) => {
  const [method, setMethod] = useState(request.method || 'GET');
  const [url, setUrl] = useState(request.url || '');
  const [tab, setTab] = useState<'params' | 'body' | 'headers'>('body');
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [headers, setHeaders] = useState<{ [k: string]: string }>(request.headers || {});
  const [params, setParams] = useState<{ [k: string]: string }>(request.params || {});
  const [pathVars, setPathVars] = useState<{ [k: string]: string }>(request.pathVars || {});
  const [body, setBody] = useState<string>(request.body || '');
  const [copied, setCopied] = useState(false);
  const syncedId = useRef<string>('');
  const liveOriginRef = useRef<string | null | undefined>(liveOrigin);
  useEffect(() => { liveOriginRef.current = liveOrigin; }, [liveOrigin]);

  // Rewrite a URL's origin (scheme/host/port) to `origin`, preserving the
  // path + query. Handles a full URL or a bare path.
  const withOrigin = (raw: string, origin?: string | null): string => {
    if (!origin) return raw;
    try {
      const parsed = new URL(raw);
      const o = new URL(origin);
      parsed.protocol = o.protocol;
      parsed.host = o.host; // includes port
      return parsed.toString();
    } catch {
      const path = raw.startsWith('/') ? raw : `/${raw}`;
      return `${origin.replace(/\/$/, '')}${path}`;
    }
  };

  // Re-sync editor when a different saved request is selected.
  useEffect(() => {
    setMethod(request.method || 'GET');
    setUrl(withOrigin(request.url || '', liveOriginRef.current));
    setHeaders(request.headers || {});
    setParams(request.params || {});
    setPathVars(request.pathVars || {});
    setBody(request.body || '');
    setResponse(request.lastResponse ?? null);
    setError(request.lastError ?? null);
    syncedId.current = request.id;
  }, [request.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the displayed URL's host/port in sync with the live server, so it
  // always reflects the port the server is actually listening on.
  useEffect(() => {
    if (!liveOrigin) return;
    setUrl((prev) => withOrigin(prev, liveOrigin));
  }, [liveOrigin]);

  // Auto-detect `:token` path variables from the URL; keep entered values.
  useEffect(() => {
    const path = (() => {
      try { return new URL(url).pathname; } catch { return url; }
    })();
    const names = Array.from(path.matchAll(/:([A-Za-z0-9_]+)/g)).map((m) => m[1]);
    setPathVars((prev) => {
      const next: { [k: string]: string } = {};
      names.forEach((n) => { next[n] = prev[n] ?? ''; });
      // avoid needless state churn
      const same = Object.keys(next).length === Object.keys(prev).length && names.every((n) => n in prev);
      return same ? prev : next;
    });
  }, [url]);

  // Persist edits back to the saved request (debounced) so they survive reopen.
  useEffect(() => {
    if (syncedId.current !== request.id) return;
    const t = setTimeout(() => onChange?.({ id: request.id, method, url, headers, params, pathVars, body }), 400);
    return () => clearTimeout(t);
  }, [method, url, headers, params, pathVars, body]); // eslint-disable-line react-hooks/exhaustive-deps

  const basePath = (raw: string): string => {
    try {
      const u = new URL(raw);
      return u.pathname + u.search;
    } catch {
      const m = raw.match(/https?:\/\/[^/]+(\/.*)/);
      return m ? m[1] : raw.startsWith('/') ? raw : `/${raw}`;
    }
  };

  const buildPath = (): string => {
    let p = basePath(url);
    // Substitute :token path variables.
    Object.entries(pathVars).forEach(([k, v]) => {
      if (v) p = p.replace(new RegExp(`:${k}(?=/|$|\\?)`, 'g'), v);
    });
    const entries = Object.entries(params).filter(([k]) => k.trim());
    if (entries.length) {
      const qs = entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
      p += (p.includes('?') ? '&' : '?') + qs;
    }
    return p;
  };

  const targetUrl = baseUrl ? `${baseUrl.replace(/\/$/, '')}${buildPath()}` : null;

  const send = async () => {
    if (!baseUrl) {
      setError('No running server. Click Run on your app first — once it starts listening on a port, requests are sent here automatically.');
      return;
    }
    setIsLoading(true);
    setError(null);
    setResponse(null);

    const h: { [k: string]: string } = { 'Content-Type': 'application/json' };
    Object.entries(headers).forEach(([k, v]) => { if (k && v) h[k] = v; });

    let bodyStr: string | undefined;
    if (method !== 'GET' && body.trim()) {
      try { bodyStr = JSON.stringify(JSON.parse(body)); } catch { bodyStr = body; }
    }

    const t0 = Date.now();
    try {
      if (containerRequest) {
        const info = await containerRequest({ method, path: buildPath(), headers: h, body: bodyStr });
        setResponse(info); onResponse?.(info);
        onChange?.({ id: request.id, lastResponse: info, lastError: null });
        return;
      }
      const res = await fetch(targetUrl as string, { method, headers: h, body: bodyStr });
      const ct = res.headers.get('content-type') || '';
      const data = ct.includes('application/json') ? await res.json() : await res.text();
      const info = { status: res.status, statusText: res.statusText, headers: Object.fromEntries(res.headers.entries()), data, time: Date.now() - t0, size: JSON.stringify(data).length };
      setResponse(info); onResponse?.(info);
      onChange?.({ id: request.id, lastResponse: info, lastError: null });
    } catch (e: any) {
      const msg = `Request failed.\n\n${e?.message || e}\n\nCheck the server is running (Terminal / Output), the route exists, and the body is valid JSON.`;
      setError(msg);
      onChange?.({ id: request.id, lastError: msg, lastResponse: null });
    } finally {
      setIsLoading(false);
    }
  };

  const curl = (() => {
    const u = url || targetUrl || buildPath();
    let c = `curl -X ${method} "${u}"`;
    Object.entries({ 'Content-Type': 'application/json', ...headers }).forEach(([k, v]) => {
      if (k && v) c += ` \\\n  -H "${k}: ${v}"`;
    });
    if (method !== 'GET' && body) {
      try { c += ` \\\n  -d '${JSON.stringify(JSON.parse(body))}'`; } catch { c += ` \\\n  -d '${body}'`; }
    }
    return c;
  })();

  const copyCurl = () => {
    navigator.clipboard.writeText(curl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // Reusable key/value editor for params + headers.
  const kvEditor = (
    obj: { [k: string]: string },
    setObj: (o: { [k: string]: string }) => void,
    addLabel: string,
  ) => (
    <div className="space-y-2">
      {Object.entries(obj).map(([key, value], i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Key"
            value={key}
            onChange={(e) => { const next = { ...obj }; delete next[key]; next[e.target.value] = value; setObj(next); }}
            className={`flex-1 ${inputCls}`}
          />
          <input
            type="text"
            placeholder="Value"
            value={value}
            onChange={(e) => setObj({ ...obj, [key]: e.target.value })}
            className={`flex-1 ${inputCls}`}
          />
          <button onClick={() => { const next = { ...obj }; delete next[key]; setObj(next); }} className="p-1 text-gray-400 hover:text-rose-500">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button onClick={() => setObj({ ...obj, '': '' })} className="inline-flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:underline">
        <Plus className="w-3.5 h-3.5" /> {addLabel}
      </button>
    </div>
  );

  return (
    <div className="flex flex-col gap-3">
      {/* Request bar */}
      <div className="api-request-bar">
        <select
          value={method}
          onChange={(e) => setMethod(e.target.value)}
          className={`api-request-method api-method px-2.5 py-0 text-sm font-bold bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg outline-none focus:ring-2 focus:ring-primary-500/40 ${methodColor(method)}`}
        >
          {METHODS.map((m) => <option key={m} value={m} className="text-gray-900">{m}</option>)}
        </select>
        <input
          type="text"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="/users/USER001"
          className="api-request-url px-3 py-0 text-sm font-mono bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400 outline-none text-gray-900 dark:text-white placeholder-gray-400"
        />
        <button
          onClick={send}
          disabled={isLoading}
          className="api-send-button inline-flex items-center justify-center gap-1.5 px-4 py-0 text-sm font-semibold rounded-lg bg-primary-600 hover:bg-primary-700 text-white transition-colors disabled:opacity-50"
        >
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Send
        </button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-800">
        {(['params', 'body', 'headers'] as const).map((t) => {
          const count = t === 'headers' ? Object.keys(headers).filter((k) => k).length
            : t === 'params' ? Object.keys(params).filter((k) => k).length + Object.keys(pathVars).length : 0;
          return (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-1.5 text-xs font-medium capitalize border-b-2 -mb-px transition-colors ${tab === t ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'}`}
            >
              {t}{count ? ` (${count})` : ''}
            </button>
          );
        })}
      </div>

      {tab === 'params' && (
        <div className="space-y-4">
          {Object.keys(pathVars).length > 0 && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">
                Path variables
                <span className="ml-1 normal-case font-normal text-gray-400">— from <span className="font-mono">:tokens</span> in the URL</span>
              </div>
              <div className="space-y-2">
                {Object.entries(pathVars).map(([key, value]) => (
                  <div key={key} className="flex items-center gap-2">
                    <span className="w-1/3 px-2 py-1.5 text-sm font-mono rounded bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 truncate">:{key}</span>
                    <input
                      type="text"
                      placeholder={`value for :${key}`}
                      value={value}
                      onChange={(e) => setPathVars({ ...pathVars, [key]: e.target.value })}
                      className={`flex-1 ${inputCls}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">Query params</div>
            {kvEditor(params, setParams, 'Add query param')}
          </div>
        </div>
      )}

      {tab === 'headers' && kvEditor(headers, setHeaders, 'Add header')}

      {tab === 'body' && (
        method === 'GET' ? (
          <p className="text-xs text-gray-400 py-2">GET requests don't send a body. Use Params for the query string.</p>
        ) : (
          <div>
            <div className="flex justify-end mb-1.5">
              <button
                onClick={() => { try { setBody(JSON.stringify(JSON.parse(body || '{}'), null, 2)); } catch { /* ignore */ } }}
                className="inline-flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline"
              >
                <Sparkles className="w-3.5 h-3.5" /> Beautify
              </button>
            </div>
            <div className="rounded-lg overflow-hidden border border-gray-700" style={{ height: 180 }}>
              <MonacoCodeEditor
                value={body}
                onChange={(v: string) => setBody(v)}
                language="json"
                filePath="request-body.json"
                height="180px"
                minimap={false}
              />
            </div>
          </div>
        )
      )}

      {/* cURL */}
      <details className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40">
        <summary className="flex items-center gap-2 px-3 py-2 cursor-pointer text-xs font-semibold text-gray-600 dark:text-gray-300 select-none">
          <Code2 className="w-3.5 h-3.5" /> cURL
          <span
            onClick={(e) => { e.preventDefault(); copyCurl(); }}
            className="ml-auto inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline"
          >
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {copied ? 'Copied' : 'Copy'}
          </span>
        </summary>
        <pre className="px-3 pb-3 text-xs overflow-x-auto font-mono text-cyan-700 dark:text-cyan-300 whitespace-pre-wrap">{curl}</pre>
      </details>

      {/* Response — Postman-style */}
      {(response || error) && (
        <div className="border-t border-gray-200 dark:border-gray-800 pt-3 flex flex-col gap-2">
          {error ? (
            <div className="p-3 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-lg flex items-start gap-2">
              <XCircle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
              <pre className="text-sm text-rose-600 dark:text-rose-400 whitespace-pre-wrap font-sans">{error}</pre>
            </div>
          ) : response && (
            <ResponsePanel response={response} />
          )}
        </div>
      )}
    </div>
  );
};

export default ApiTester;
